import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { usePortalRole } from '../../contexts/PortalRoleContext';
import { listCreditAudits, formatAmount, getTransferTypeLabel, formatDate } from '../../api/creditAuditPortalApi';
import { listCampuses } from '../../api/campusPortalApi';
import StudentBillingSummary from '../../components/Portal/StudentBillingSummary';
import DiscountOptionsManager from '../../components/Portal/DiscountOptionsManager';
import AttachmentPreviewModal from '../../components/Portal/AttachmentPreviewModal';
import * as S from './Management.styles';

const BillingManagement = () => {
  const auth = useAuth();
  const { currentUserCampus } = usePortalRole();

  // Get user's Cognito groups
  const userGroups = auth.user?.profile['cognito:groups'] || [];
  const portalGroups = userGroups.filter(g => g.startsWith('portal_'));

  // Permission checks - Director, System Admin, and Manager
  const isDeveloper = portalGroups.includes('portal_developer');
  const isDirector = portalGroups.includes('portal_director');
  const isSystemAdmin = portalGroups.includes('portal_system_admin');
  const isManager = portalGroups.includes('portal_manager');
  const canView = isDeveloper || isDirector || isSystemAdmin || isManager;
  const canFilterAllCampuses = isDeveloper || isSystemAdmin;

  // Main tab state
  const [mainTab, setMainTab] = useState('transactions'); // transactions, summaries, discounts

  // Data states
  const [audits, setAudits] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter states
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [campusFilter, setCampusFilter] = useState(currentUserCampus || 'all');
  const [viewMode, setViewMode] = useState('ledger'); // 'ledger' or 'transfers'

  // Update campus filter when currentUserCampus becomes available (async profile load)
  useEffect(() => {
    if (currentUserCampus && !canFilterAllCampuses) {
      setCampusFilter(currentUserCampus);
    }
  }, [currentUserCampus, canFilterAllCampuses]);

  // Attachment preview state
  const [previewAuditId, setPreviewAuditId] = useState(null);

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!canView) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [auditsResult, campusesData] = await Promise.all([
        listCreditAudits({
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          studentSearch: studentSearch || undefined,
          campusId: campusFilter !== 'all' ? campusFilter : undefined
        }),
        listCampuses()
      ]);
      setAudits(auditsResult.audits || []);
      setCampuses(campusesData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [canView, dateFrom, dateTo, studentSearch, campusFilter]);

  // Fetch data on mount and when filters change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Computed data - split by transfer type
  const ledgerRecords = audits.filter(a => a.transferType === 'normal');
  const transferRecords = audits.filter(a =>
    a.transferType === 'from_student' || a.transferType === 'to_student'
  );

  // Current records based on view mode
  const currentRecords = viewMode === 'ledger' ? ledgerRecords : transferRecords;

  // Statistics for ledger (external transactions)
  const totalIncome = ledgerRecords
    .filter(a => parseFloat(a.delta) > 0)
    .reduce((sum, a) => sum + parseFloat(a.delta), 0);
  const totalExpense = ledgerRecords
    .filter(a => parseFloat(a.delta) < 0)
    .reduce((sum, a) => sum + Math.abs(parseFloat(a.delta)), 0);
  const netBalance = totalIncome - totalExpense;

  // Statistics for transfers
  const totalToStudent = transferRecords
    .filter(a => a.transferType === 'to_student')
    .reduce((sum, a) => sum + Math.abs(parseFloat(a.delta)), 0);
  const totalFromStudent = transferRecords
    .filter(a => a.transferType === 'from_student')
    .reduce((sum, a) => sum + parseFloat(a.delta), 0);

  // Show loading
  if (auth.isLoading || loading) {
    return (
      <S.Container>
        <S.Header>
          <div>
            <S.Title>Billing Management</S.Title>
            <S.Subtitle>Loading...</S.Subtitle>
          </div>
        </S.Header>
      </S.Container>
    );
  }

  // Show permission denied
  if (!canView) {
    return (
      <S.Container>
        <S.Header>
          <div>
            <S.Title>Billing Management</S.Title>
            <S.Subtitle>Access Restricted</S.Subtitle>
          </div>
        </S.Header>
        <S.ErrorBanner>
          You do not have permission to view billing records. This page is only available to Directors, System Administrators, and Managers.
        </S.ErrorBanner>
      </S.Container>
    );
  }

  // Render tab content
  const renderTabContent = () => {
    switch (mainTab) {
      case 'summaries':
        return <StudentBillingSummary />;
      case 'discounts':
        return <DiscountOptionsManager />;
      case 'transactions':
      default:
        return renderTransactionsTab();
    }
  };

  // Render transactions tab
  const renderTransactionsTab = () => (
    <>
      {/* Statistics Cards */}
      <S.StatsGrid style={{ marginBottom: '24px' }}>
        <S.StatCard color="#10b981">
          <S.StatNumber>${totalIncome.toFixed(2)}</S.StatNumber>
          <S.StatLabel>Total Income</S.StatLabel>
          <S.StatSubtext>External credits added</S.StatSubtext>
        </S.StatCard>
        <S.StatCard color="#ef4444">
          <S.StatNumber>${totalExpense.toFixed(2)}</S.StatNumber>
          <S.StatLabel>Total Expense</S.StatLabel>
          <S.StatSubtext>External credits removed</S.StatSubtext>
        </S.StatCard>
        <S.StatCard color="#3b82f6">
          <S.StatNumber style={{ color: netBalance >= 0 ? '#10b981' : '#ef4444' }}>
            ${netBalance.toFixed(2)}
          </S.StatNumber>
          <S.StatLabel>Net Balance</S.StatLabel>
          <S.StatSubtext>Income - Expense</S.StatSubtext>
        </S.StatCard>
        <S.StatCard color="#F1AF59">
          <S.StatNumber>{transferRecords.length}</S.StatNumber>
          <S.StatLabel>Balance Transfers</S.StatLabel>
          <S.StatSubtext>Between student and courses</S.StatSubtext>
        </S.StatCard>
      </S.StatsGrid>

      {/* View Mode Toggle */}
      <S.ButtonGroup style={{ marginBottom: '16px' }}>
        <S.Button
          color={viewMode === 'ledger' ? '#F1AF59' : '#6b7280'}
          onClick={() => setViewMode('ledger')}
        >
          Ledger (External)
        </S.Button>
        <S.Button
          color={viewMode === 'transfers' ? '#F1AF59' : '#6b7280'}
          onClick={() => setViewMode('transfers')}
        >
          Balance Transfers
        </S.Button>
      </S.ButtonGroup>

      {/* Filters Bar */}
      <S.FiltersBar>
        <S.SearchInput
          type="text"
          placeholder="Search by student name..."
          value={studentSearch}
          onChange={(e) => setStudentSearch(e.target.value)}
        />
        <S.Input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          style={{ width: '150px' }}
        />
        <S.Input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          style={{ width: '150px' }}
        />
        {canFilterAllCampuses ? (
          <S.FilterSelect
            value={campusFilter}
            onChange={(e) => setCampusFilter(e.target.value)}
          >
            <option value="all">All Campuses</option>
            {campuses.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </S.FilterSelect>
        ) : (
          <S.Badge color="#6b7280" style={{ alignSelf: 'center', padding: '10px 16px' }}>
            Your Campus Only
          </S.Badge>
        )}
      </S.FiltersBar>

      {/* Records Table */}
      <S.TableContainer>
        <S.Table $cards>
          <thead>
            <tr>
              <S.Th>Date</S.Th>
              <S.Th>Student</S.Th>
              <S.Th>Course Group</S.Th>
              <S.Th>Amount</S.Th>
              <S.Th>Description</S.Th>
              <S.Th>Operator</S.Th>
              {viewMode === 'transfers' && <S.Th>Type</S.Th>}
              <S.Th>Attachment</S.Th>
            </tr>
          </thead>
          <tbody>
            {currentRecords.length === 0 ? (
              <tr>
                <S.Td colSpan={viewMode === 'transfers' ? 8 : 7} style={{ textAlign: 'center', color: '#6b7280' }}>
                  No records found
                </S.Td>
              </tr>
            ) : (
              currentRecords.map(audit => (
                <tr key={audit.id}>
                  <S.Td data-label="Date">{formatDate(audit.createdAt)}</S.Td>
                  <S.Td data-label="Student"><S.UserName>{audit.studentName}</S.UserName></S.Td>
                  <S.Td data-label="Course Group">{audit.courseGroupName || '-'}</S.Td>
                  <S.Td data-label="Amount">
                    <S.Badge color={parseFloat(audit.delta) >= 0 ? '#10b981' : '#ef4444'}>
                      {formatAmount(audit.delta)}
                    </S.Badge>
                  </S.Td>
                  <S.Td data-label="Description">
                    <span style={{ display: 'block', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {audit.description}
                    </span>
                  </S.Td>
                  <S.Td data-label="Operator">{audit.operatorName}</S.Td>
                  {viewMode === 'transfers' && (
                    <S.Td data-label="Type">
                      <S.Badge color="#3b82f6">
                        {getTransferTypeLabel(audit.transferType)}
                      </S.Badge>
                    </S.Td>
                  )}
                  <S.Td data-label="Attachment">
                    {audit.attachment ? (
                      <S.ActionButton
                        color="#3b82f6"
                        onClick={() => setPreviewAuditId(audit.id)}
                      >
                        View
                      </S.ActionButton>
                    ) : (
                      <span style={{ color: '#9ca3af' }}>-</span>
                    )}
                  </S.Td>
                </tr>
              ))
            )}
          </tbody>
        </S.Table>
      </S.TableContainer>

      {/* Attachment Preview Modal */}
      {previewAuditId && (
        <AttachmentPreviewModal
          auditId={previewAuditId}
          onClose={() => setPreviewAuditId(null)}
        />
      )}

      {/* Summary Stats */}
      <S.Stats>
        Showing {currentRecords.length} records
        {viewMode === 'ledger' && (
          <> | Income: ${totalIncome.toFixed(2)} | Expense: ${totalExpense.toFixed(2)}</>
        )}
        {viewMode === 'transfers' && (
          <> | To Balance: ${totalToStudent.toFixed(2)} | From Balance: ${totalFromStudent.toFixed(2)}</>
        )}
      </S.Stats>

      {/* Info Section */}
      <S.Card style={{ marginTop: '24px' }}>
        <S.CardHeader>
          <h3>{viewMode === 'ledger' ? 'Ledger Information' : 'Balance Transfer Information'}</h3>
        </S.CardHeader>
        <S.InfoGrid>
          {viewMode === 'ledger' ? (
            <>
              <S.InfoItem>
                <S.InfoLabel>Income Records</S.InfoLabel>
                <S.InfoValue>{ledgerRecords.filter(a => parseFloat(a.delta) > 0).length}</S.InfoValue>
              </S.InfoItem>
              <S.InfoItem>
                <S.InfoLabel>Expense Records</S.InfoLabel>
                <S.InfoValue>{ledgerRecords.filter(a => parseFloat(a.delta) < 0).length}</S.InfoValue>
              </S.InfoItem>
              <S.InfoItem>
                <S.InfoLabel>Total Records</S.InfoLabel>
                <S.InfoValue>{ledgerRecords.length}</S.InfoValue>
              </S.InfoItem>
              <S.InfoItem>
                <S.InfoLabel>Average Transaction</S.InfoLabel>
                <S.InfoValue>
                  ${ledgerRecords.length > 0
                    ? (ledgerRecords.reduce((sum, a) => sum + Math.abs(parseFloat(a.delta)), 0) / ledgerRecords.length).toFixed(2)
                    : '0.00'}
                </S.InfoValue>
              </S.InfoItem>
            </>
          ) : (
            <>
              <S.InfoItem>
                <S.InfoLabel>To Student Balance</S.InfoLabel>
                <S.InfoValue>{transferRecords.filter(a => a.transferType === 'to_student').length}</S.InfoValue>
              </S.InfoItem>
              <S.InfoItem>
                <S.InfoLabel>From Student Balance</S.InfoLabel>
                <S.InfoValue>{transferRecords.filter(a => a.transferType === 'from_student').length}</S.InfoValue>
              </S.InfoItem>
              <S.InfoItem>
                <S.InfoLabel>Total Transfers</S.InfoLabel>
                <S.InfoValue>{transferRecords.length}</S.InfoValue>
              </S.InfoItem>
              <S.InfoItem>
                <S.InfoLabel>Net Transfer</S.InfoLabel>
                <S.InfoValue style={{ color: (totalFromStudent - totalToStudent) >= 0 ? '#10b981' : '#ef4444' }}>
                  ${(totalFromStudent - totalToStudent).toFixed(2)}
                </S.InfoValue>
              </S.InfoItem>
            </>
          )}
        </S.InfoGrid>
      </S.Card>

      <div style={{ marginTop: '24px', padding: '16px', background: '#e0f2fe', borderRadius: '8px', fontSize: '13px' }}>
        <strong>About this page:</strong> This page displays all credit audit records.
        The <strong>Ledger</strong> view shows external transactions (direct credit additions/deductions not involving student balance).
        The <strong>Balance Transfers</strong> view shows transactions between student balance and course credits.
      </div>
    </>
  );

  return (
    <S.Container>
      <S.Header>
        <div>
          <S.Title>Billing Management</S.Title>
          <S.Subtitle>Manage transactions, student billing, and discounts</S.Subtitle>
        </div>
      </S.Header>

      {error && (
        <S.ErrorBanner>
          Error loading data: {error}
        </S.ErrorBanner>
      )}

      {/* Main Tabs */}
      <S.ButtonGroup style={{ marginBottom: '24px' }}>
        <S.Button
          color={mainTab === 'transactions' ? '#F1AF59' : '#6b7280'}
          onClick={() => setMainTab('transactions')}
        >
          Transactions
        </S.Button>
        <S.Button
          color={mainTab === 'summaries' ? '#F1AF59' : '#6b7280'}
          onClick={() => setMainTab('summaries')}
        >
          Student Summaries
        </S.Button>
        <S.Button
          color={mainTab === 'discounts' ? '#F1AF59' : '#6b7280'}
          onClick={() => setMainTab('discounts')}
        >
          Discount Options
        </S.Button>
      </S.ButtonGroup>

      {/* Tab Content */}
      {renderTabContent()}
    </S.Container>
  );
};

export default BillingManagement;
